import { useEffect, useState } from "react";
import {
  Link2, Unplug, RefreshCw, Wand2, ChevronDown, Check, Copy, ExternalLink, ShieldCheck, Phone, GitBranch,
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import ConfirmDialog from "@/components/ConfirmDialog";

const STATUS_LABELS = {
  new: "New",
  appointment_set: "Appointment set",
  offer_made: "Offer made",
  under_contract: "Under contract",
  closed: "Closed",
  dead: "Dead",
};

const GHL_PERMISSIONS = "View Locations, Edit Contacts, View Conversations, View Conversation Messages, View Opportunities, Edit Opportunities";

const Steps = ({ title, children, testid }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="mt-3" data-testid={testid}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-1.5 text-xs transition hover:opacity-80"
        style={{ color: "var(--lime)" }}
      >
        <ChevronDown size={13} style={{ transform: open ? "rotate(180deg)" : "none", transition: "transform 0.2s" }} />
        {title}
      </button>
      {open && (
        <div className="mt-3 p-4 rounded-2xl text-xs space-y-2"
          style={{ background: "var(--surface2)", border: "1px solid var(--border)", color: "var(--text-muted)" }}>
          {children}
        </div>
      )}
    </div>
  );
};

const Card = ({ icon: Icon, title, subtitle, connected, children, testid }) => (
  <div className="surface-card p-6 mb-6" data-testid={testid}>
    <div className="flex items-start gap-4 mb-5">
      <div className="w-11 h-11 rounded-2xl flex items-center justify-center shrink-0"
        style={{ background: connected ? "var(--lime-glow)" : "var(--surface2)", color: connected ? "var(--lime)" : "var(--text-muted)" }}>
        <Icon size={19} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <h2 className="h-display text-xl">{title}</h2>
          {connected && (
            <span className="font-mono text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full flex items-center gap-1"
              style={{ background: "var(--lime-glow)", color: "var(--lime)", border: "1px solid var(--lime)" }}>
              <Check size={10} /> Connected
            </span>
          )}
        </div>
        <p className="text-sm mt-1" style={{ color: "var(--text-muted)" }}>{subtitle}</p>
      </div>
    </div>
    {children}
  </div>
);

export const ConnectTools = () => {
  const [ghl, setGhl] = useState(null);
  const [bd, setBd] = useState(null);
  const [ghlForm, setGhlForm] = useState({ location_id: "", token: "" });
  const [bdKey, setBdKey] = useState("");
  const [pipelines, setPipelines] = useState([]);
  const [mapping, setMapping] = useState({ pipeline_id: "", stage_map: {} });
  const [showStages, setShowStages] = useState(false);
  const [busy, setBusy] = useState("");
  const [confirm, setConfirm] = useState(null);

  const load = async () => {
    const [{ data: g }, { data: b }] = await Promise.all([
      api.get("/ghl/status"),
      api.get("/batchdialer/status"),
    ]);
    setGhl(g);
    setBd(b);
    if (g.connected) {
      setMapping({ pipeline_id: g.pipeline_id || "", stage_map: g.stage_map || {} });
      try {
        const { data: pls } = await api.get("/ghl/pipelines");
        setPipelines(pls);
      } catch (e) { /* their token may have been revoked */ }
    }
  };

  useEffect(() => { load(); }, []);

  const run = async (key, fn, msg) => {
    setBusy(key);
    try {
      const result = await fn();
      if (msg) toast.success(typeof msg === "function" ? msg(result) : msg);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Something went wrong");
    } finally {
      setBusy("");
    }
  };

  const selectedPipeline = pipelines.find((p) => p.id === mapping.pipeline_id);
  const mappedCount = Object.values(ghl?.stage_map || {}).filter(Boolean).length;

  if (!ghl || !bd) {
    return <div className="flex justify-center py-16"><span className="spinner" /></div>;
  }

  return (
    <div className="mb-6" data-testid="integrations-page">
      <div className="flex items-center gap-2 mb-1">
        <Link2 size={16} style={{ color: "var(--lime)" }} />
        <div className="font-mono text-xs uppercase tracking-wider" style={{ color: "var(--lime)" }}>
          Connect your tools
        </div>
      </div>
      <p className="text-sm mb-6" style={{ color: "var(--text-muted)" }}>
        Two things to paste, once. After that your leads, calls and texts flow in on their own and your KPIs
        show real numbers instead of guesses.
      </p>

      {/* ---------------- GoHighLevel ---------------- */}
      <Card
        icon={Link2}
        title="GoHighLevel"
        subtitle={ghl.connected ? (ghl.location_name || ghl.location_id) : "Leads sync both ways, calls and texts feed your KPIs"}
        connected={ghl.connected}
        testid="ghl-card"
      >
        {!ghl.connected ? (
          <>
            <div className="space-y-3">
              <div>
                <label className="field-label">1. Paste your GoHighLevel link or Location ID</label>
                <input
                  className="input-base w-full"
                  placeholder="Paste the address bar from GoHighLevel, or just the ID"
                  value={ghlForm.location_id}
                  onChange={(e) => setGhlForm({ ...ghlForm, location_id: e.target.value })}
                  data-testid="ghl-location-input"
                />
                <p className="text-[11px] mt-1.5" style={{ color: "var(--text-dim)" }}>
                  Easiest way: open GoHighLevel, copy the whole web address, paste it here. We pull the ID out for you.
                </p>
              </div>
              <div>
                <label className="field-label">2. Paste your Private Integration token</label>
                <input
                  type="password"
                  autoComplete="off"
                  className="input-base w-full"
                  placeholder="Starts with pit-"
                  value={ghlForm.token}
                  onChange={(e) => setGhlForm({ ...ghlForm, token: e.target.value })}
                  data-testid="ghl-token-input"
                />
              </div>
            </div>

            <button
              onClick={() => run("ghl-connect", async () => {
                if (!ghlForm.location_id.trim() || !ghlForm.token.trim()) throw new Error("Paste both boxes above");
                const { data } = await api.post("/ghl/connect", ghlForm);
                setGhlForm({ location_id: "", token: "" });
                return data;
              }, "GoHighLevel connected. Your leads are syncing now")}
              disabled={busy === "ghl-connect"}
              className="btn-primary mt-5 flex items-center gap-2"
              data-testid="ghl-connect-button"
            >
              {busy === "ghl-connect" ? <span className="spinner" /> : <Link2 size={15} />} Connect
            </button>

            <Steps title="Where do I get the token?" testid="ghl-help">
              <p>1. In GoHighLevel, click Settings in the left menu.</p>
              <p>2. Click Private Integrations, then Create new Integration. Name it &quot;Wholesalers University&quot;.</p>
              <p className="flex items-start gap-2 flex-wrap">
                3. Tick these boxes:
                <span style={{ color: "var(--text)" }}>{GHL_PERMISSIONS}</span>
                <button
                  onClick={() => { navigator.clipboard.writeText(GHL_PERMISSIONS); toast.success("Copied"); }}
                  className="inline-flex items-center gap-1" style={{ color: "var(--lime)" }}
                  data-testid="ghl-copy-permissions"
                >
                  <Copy size={11} /> copy
                </button>
              </p>
              <p>4. Click Create, then copy the token it shows you. It is only shown once.</p>
              <p>5. Paste it above. That is it, we handle the rest.</p>
            </Steps>
          </>
        ) : (
          <>
            <div className="flex items-center gap-2 flex-wrap mb-4">
              <button
                onClick={() => run("ghl-automap", async () => (await api.post("/ghl/auto-map")).data,
                  (r) => `${r.matched} stages matched automatically`)}
                disabled={busy === "ghl-automap"}
                className="btn-primary text-sm flex items-center gap-2"
                data-testid="ghl-auto-map"
              >
                {busy === "ghl-automap" ? <span className="spinner" /> : <Wand2 size={14} />} Match my pipeline for me
              </button>
              <button
                onClick={() => run("ghl-activity", async () => (await api.post("/ghl/sync-activity?days=14")).data,
                  (r) => `Pulled ${r.totals.calls} calls and ${r.totals.texts} texts`)}
                disabled={busy === "ghl-activity"}
                className="btn-ghost text-sm flex items-center gap-2"
                data-testid="ghl-sync-activity"
              >
                {busy === "ghl-activity" ? <span className="spinner" /> : <RefreshCw size={14} />} Sync my numbers
              </button>
              <button
                onClick={() => run("ghl-pipeline", async () => (await api.post("/ghl/sync-pipeline")).data,
                  (r) => r.updated.length ? `${r.updated.length} lead(s) updated` : "Everything already matches")}
                disabled={busy === "ghl-pipeline"}
                className="btn-ghost text-sm flex items-center gap-2"
                data-testid="ghl-sync-pipeline"
              >
                {busy === "ghl-pipeline" ? <span className="spinner" /> : <GitBranch size={14} />} Pull stage changes
              </button>
            </div>

            <div className="text-xs font-mono mb-1" style={{ color: "var(--text-muted)" }} data-testid="ghl-summary">
              {ghl.pipeline_name ? `${ghl.pipeline_name} · ${mappedCount} stages mapped` : "No pipeline mapped yet"}
              {ghl.last_sync_at && ` · last sync ${new Date(ghl.last_sync_at).toLocaleString()}`}
            </div>

            <button
              onClick={() => setShowStages((v) => !v)}
              className="text-xs transition hover:opacity-80"
              style={{ color: "var(--lime)" }}
              data-testid="ghl-toggle-stages"
            >
              {showStages ? "Hide stage mapping" : "Change stage mapping myself"}
            </button>

            {showStages && (
              <div className="mt-4">
                <select
                  className="input-base w-full mb-3"
                  style={{ background: "var(--surface)", color: "var(--text)" }}
                  value={mapping.pipeline_id}
                  onChange={(e) => setMapping({ pipeline_id: e.target.value, stage_map: {} })}
                  data-testid="ghl-pipeline-select"
                >
                  <option value="">Choose a pipeline...</option>
                  {pipelines.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>

                {selectedPipeline && (
                  <div className="space-y-2">
                    {Object.entries(STATUS_LABELS).map(([key, label]) => (
                      <div key={key} className="flex items-center gap-3 flex-wrap">
                        <span className="text-sm w-32 shrink-0">{label}</span>
                        <select
                          className="input-base flex-1 min-w-[170px] text-sm py-2"
                          style={{ background: "var(--surface)", color: "var(--text)" }}
                          value={mapping.stage_map[key] || ""}
                          onChange={(e) => setMapping((m) => ({ ...m, stage_map: { ...m.stage_map, [key]: e.target.value } }))}
                          data-testid={`ghl-stage-${key}`}
                        >
                          <option value="">Not mapped</option>
                          {selectedPipeline.stages.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                        </select>
                      </div>
                    ))}
                    <button
                      onClick={() => run("ghl-mapping", async () => { await api.put("/ghl/pipeline-mapping", mapping); }, "Mapping saved")}
                      disabled={busy === "ghl-mapping"}
                      className="btn-primary text-sm mt-2"
                      data-testid="ghl-save-mapping"
                    >
                      {busy === "ghl-mapping" ? <span className="spinner" /> : "Save mapping"}
                    </button>
                  </div>
                )}
              </div>
            )}

            <button
              onClick={() => setConfirm("ghl")}
              className="text-xs mt-5 flex items-center gap-1.5 transition hover:opacity-80"
              style={{ color: "var(--text-dim)" }}
              data-testid="ghl-disconnect-button"
            >
              <Unplug size={12} /> Disconnect GoHighLevel
            </button>
          </>
        )}
      </Card>

      {/* ---------------- BatchDialer ---------------- */}
      <Card
        icon={Phone}
        title="BatchDialer"
        subtitle={bd.connected ? "Your dials count themselves now" : "Your real dial count and talk time, straight into your KPIs"}
        connected={bd.connected}
        testid="bd-card"
      >
        {!bd.connected ? (
          <>
            <label className="field-label">Paste your BatchDialer API key</label>
            <input
              type="password"
              autoComplete="off"
              className="input-base w-full"
              placeholder="API key from BatchDialer settings"
              value={bdKey}
              onChange={(e) => setBdKey(e.target.value)}
              data-testid="bd-key-input"
            />
            <button
              onClick={() => run("bd-connect", async () => {
                if (!bdKey.trim()) throw new Error("Paste your API key first");
                const { data } = await api.post("/batchdialer/connect", { api_key: bdKey.trim() });
                setBdKey("");
                return data;
              }, "BatchDialer connected")}
              disabled={busy === "bd-connect"}
              className="btn-primary mt-4 flex items-center gap-2"
              data-testid="bd-connect-button"
            >
              {busy === "bd-connect" ? <span className="spinner" /> : <Phone size={15} />} Connect
            </button>

            <Steps title="Where do I get the API key?" testid="bd-help">
              <p>1. Log in to BatchDialer and click your user icon in the top right.</p>
              <p>2. Open Settings, then look for API or Integrations.</p>
              <p>3. Copy the API key shown there and paste it above.</p>
              <p>
                If you cannot find it, message support in BatchDialer and ask for your API key. Paste it here and
                your dials start counting on their own.
              </p>
            </Steps>
          </>
        ) : (
          <>
            <button
              onClick={() => run("bd-activity", async () => (await api.post("/batchdialer/sync-activity?days=14")).data,
                (r) => r.totals.dials ? `Pulled ${r.totals.dials} dials and ${r.totals.talk_minutes} minutes of talk time` : "No dials found in the last 14 days")}
              disabled={busy === "bd-activity"}
              className="btn-primary text-sm flex items-center gap-2"
              data-testid="bd-sync-activity"
            >
              {busy === "bd-activity" ? <span className="spinner" /> : <RefreshCw size={14} />} Sync my dials
            </button>
            <div className="text-xs font-mono mt-3" style={{ color: "var(--text-muted)" }} data-testid="bd-summary">
              Key ...{bd.key_fingerprint}
              {bd.last_sync_at && ` · last sync ${new Date(bd.last_sync_at).toLocaleString()}`}
            </div>

            <Steps title="Dials not showing up? Use the live feed instead" testid="bd-webhook-help">
              <p>
                In BatchDialer (or Zapier), add a webhook on the call disposition event and point it at this
                address. Every call then counts the moment it happens.
              </p>
              <div className="flex items-center gap-2 flex-wrap">
                <code className="break-all" style={{ color: "var(--text)" }}>{bd.webhook_url}</code>
                <button
                  onClick={() => { navigator.clipboard.writeText(bd.webhook_url); toast.success("Copied"); }}
                  className="inline-flex items-center gap-1" style={{ color: "var(--lime)" }}
                  data-testid="bd-copy-webhook"
                >
                  <Copy size={11} /> copy
                </button>
              </div>
            </Steps>

            <button
              onClick={() => setConfirm("bd")}
              className="text-xs mt-5 flex items-center gap-1.5 transition hover:opacity-80"
              style={{ color: "var(--text-dim)" }}
              data-testid="bd-disconnect-button"
            >
              <Unplug size={12} /> Disconnect BatchDialer
            </button>
          </>
        )}
      </Card>

      <div className="flex items-start gap-2 text-xs" style={{ color: "var(--text-dim)" }}>
        <ShieldCheck size={13} className="mt-0.5 shrink-0" />
        Both keys are encrypted before they are stored, are never shown again, and are never visible to your coach.
        Disconnect any time and the key is deleted.
      </div>

      <a
        href="https://help.gohighlevel.com/support/solutions/articles/155000003054-private-integrations"
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-1.5 text-xs mt-4 transition hover:opacity-80"
        style={{ color: "var(--text-muted)" }}
      >
        GoHighLevel&apos;s own guide to Private Integrations <ExternalLink size={12} />
      </a>

      <ConfirmDialog
        open={!!confirm}
        title={confirm === "ghl" ? "Disconnect GoHighLevel?" : "Disconnect BatchDialer?"}
        description="The saved key is deleted. Nothing already synced is removed, it just stops updating."
        destructive
        confirmLabel="Disconnect"
        onCancel={() => setConfirm(null)}
        onConfirm={async () => {
          const which = confirm;
          setConfirm(null);
          await run("disconnect", async () => {
            await api.delete(which === "ghl" ? "/ghl/disconnect" : "/batchdialer/disconnect");
          }, "Disconnected");
        }}
      />
    </div>
  );
};

export default ConnectTools;
